import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getHostEvents } from '../../api/host'
import { defaultAuthHandler } from '../../auth'
import { Features } from '../../features'
import { useTypedSelector } from '../../redux/reducers'
import {
  getNotificationPause,
  getPmitcSessionLogoutLink,
  getPmitcSessionName,
  getPmitcSessionWindow,
  isFeatureOn,
} from '../../redux/selectors'
import AuthRole from '../../role'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import { EditCustomerFunction, HostData, HostEvent } from '../Customer/Data'
import Hamburger from '../Hamburger'
import PauseNotificationsModal from '../PauseNotificationsModal/PauseNotificationsModal'
import PmitcSessionModal from '../Pmitc/PmitcSessionModal'
import Toaster from '../Toaster'
import EventLog from './EventLog'
import EventLogSummary from './EventLogSummary'
import Account from './Pmitc/Account'
import AccountSummary from './Pmitc/AccountSummary'
import FeatureUsage from './Pmitc/FeatureUsage'
import FeatureUsageSummary from './Pmitc/FeatureUsageSummary'
import Manage, { managePmitcCustomer } from './Pmitc/Manage'
import ServiceHealth from './Pmitc/ServiceHealth'
import ServiceHealthSummary from './Pmitc/ServiceHealthSummary'
import SystemInfo from './Pmitc/SystemInfo'

interface Props {
  data: HostData
  teamID: string
  editCustomer: EditCustomerFunction
  deleteCustomer: (ID: string) => void
}

const PmitcOrg: FunctionComponent<Props> = props => {
  const accountSummaryPanelName = 'account'
  const serviceHealthSummaryPanelName = 'service-health'
  const featureUsageSummaryPanelName = 'feature-usage'
  const eventLogPanelName = 'event-log'
  const [activePanel, setActivePanel] = useState('')
  const canRemove = AuthRole.hasPermission('manageCustomer')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const notificationPauseFinishTime = useTypedSelector(getNotificationPause(props.data.id))?.finishesAt
  const dispatch = useDispatch()

  const onPanelClosed = () => setActivePanel('')
  const onPanelClicked = (panelName: string) => {
    if (activePanel === panelName) {
      setActivePanel('')
      return
    }

    setActivePanel(panelName)
  }

  const onAccountSummaryClicked = () => onPanelClicked(accountSummaryPanelName)
  const onServiceHealthSummaryClicked = () => onPanelClicked(serviceHealthSummaryPanelName)
  const onFeatureUsageSummaryClicked = () => onPanelClicked(featureUsageSummaryPanelName)
  const onEventLogClicked = () => onPanelClicked(eventLogPanelName)

  const [hostEvents, setHostEvents] = useState<HostEvent[]>([])

  const loadHostEvents = useCallback(
    (signal: AbortSignal) => {
      getHostEvents(props.teamID, props.data.id, signal)
        .then(events => setHostEvents(events))
        .catch(_ => {
          if (!signal.aborted) {
            Toaster.notifyFailure('Unable to load events.')
          }
        })
    },
    [props.teamID, props.data.id]
  )

  const [showEndPmitcSessionConfirmModal, setShowEndPmitcSessionConfirmModal] = useState(false)
  const existingSessionLogoutLink = useTypedSelector(getPmitcSessionLogoutLink)
  const existingSessionName = useTypedSelector(getPmitcSessionName)
  const existingSessionWindow = useTypedSelector(getPmitcSessionWindow)

  const [showPauseNotificationsModal, setShowPauseNotificationsModal] = useState(false)

  useEffect(() => {
    const randomIntervalSeconds = (min: number, max: number) => {
      return 1000 * randomInt(min, max)
    }

    const randomInt = (min: number, max: number) => {
      return Math.floor(Math.random() * (max - min + 1)) + min
    }

    if (activePanel === eventLogPanelName) {
      const controller = new AbortController()
      loadHostEvents(controller.signal)
      const hostEventTimer = setInterval(() => loadHostEvents(controller.signal), randomIntervalSeconds(50, 70))

      return () => {
        controller.abort()
        clearInterval(hostEventTimer)
      }
    }
  }, [activePanel, loadHostEvents])

  const shorterPausesEnabled = useTypedSelector(isFeatureOn(Features.NotificationPauseTesting))

  return (
    <article id={props.data.id} className='customer'>
      <div className='flex flex-nowrap fp'>
        <Manage teamID={props.teamID} host={props.data} />
        <header>
          <div className='specs'>
            <SystemInfo
              data={props.data}
              teamID={props.teamID}
              editCustomer={props.editCustomer}
              notificationPauseFinishTime={notificationPauseFinishTime}
            />

            {canRemove && (
              <Hamburger>
                <ul>
                  {
                    <li className='trash'>
                      <a onClick={() => setShowConfirmationModal(true)}>Remove customer</a>
                    </li>
                  }
                </ul>
              </Hamburger>
            )}
          </div>

          <div className='tabs article-nav'>
            <ul>
              <AccountSummary onClick={onAccountSummaryClicked} isOpen={activePanel === accountSummaryPanelName} />
              <ServiceHealthSummary
                onClick={onServiceHealthSummaryClicked}
                isOpen={activePanel === serviceHealthSummaryPanelName}
              />
              <FeatureUsageSummary
                onClick={onFeatureUsageSummaryClicked}
                isOpen={activePanel === featureUsageSummaryPanelName}
              />
              <EventLogSummary onClick={onEventLogClicked} isOpen={activePanel === eventLogPanelName} />
            </ul>
          </div>
        </header>
      </div>
      {activePanel === accountSummaryPanelName && (
        <Account onClose={onPanelClosed} data={props.data} teamID={props.teamID} editCustomer={props.editCustomer} />
      )}
      {activePanel === serviceHealthSummaryPanelName && <ServiceHealth onClose={onPanelClosed} />}
      {activePanel === featureUsageSummaryPanelName && <FeatureUsage onClose={onPanelClosed} />}
      {activePanel === eventLogPanelName && (
        <EventLog teamID={props.teamID} hostEvents={hostEvents} onClose={onPanelClosed} />
      )}
      {showEndPmitcSessionConfirmModal && (
        <PmitcSessionModal
          customer={existingSessionName}
          onClose={() => setShowEndPmitcSessionConfirmModal(false)}
          onYes={() => {
            if (existingSessionWindow && existingSessionLogoutLink) {
              existingSessionWindow.location.href = existingSessionLogoutLink
            }

            managePmitcCustomer(dispatch, props.teamID, props.data, defaultAuthHandler.getUserName(), false)
          }}
        />
      )}

      {showConfirmationModal && (
        <ConfirmationModal
          title={`Remove ${props.data.name}`}
          message={`Do you really want to remove ${props.data.name}? This will not delete the org instance.`}
          yesLabel='Remove customer'
          onClose={() => setShowConfirmationModal(false)}
          onYes={() => props.deleteCustomer(props.data.id)}
        />
      )}

      <PauseNotificationsModal
        close={() => setShowPauseNotificationsModal(false)}
        finishTime={notificationPauseFinishTime}
        host={props.data}
        isVisible={showPauseNotificationsModal}
        teamID={props.teamID}
        testing={shorterPausesEnabled}
      />
    </article>
  )
}

export default PmitcOrg
